import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import clsx from "clsx";
import { withStyles } from "@material-ui/core/styles";
import Popper from "@material-ui/core/Popper";
import ListSubheader from "@material-ui/core/ListSubheader";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import CloseIcon from "@material-ui/icons/Close";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import useAutocomplete from "@material-ui/lab/useAutocomplete";
// styles is a runtime export of MUI's Autocomplete module but isn't in the
// typings file. Borrowing it keeps the `MuiAutocomplete-*` class names
// identical to stock MUI, so tenant CSS overrides keep working.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — named export exists in @material-ui/lab/Autocomplete/Autocomplete.js
import { styles as muiAutocompleteStyles } from "@material-ui/lab/Autocomplete/Autocomplete";
/**
 * MUI v4 Autocomplete emits the ARIA 1.1 combobox pattern: role="combobox",
 * aria-owns, aria-expanded land on the outer wrapper <div>, while the inner
 * <input> is just an input. Browse-mode NVDA treats the wrapper atomically
 * and skips the input's <label htmlFor> — field announces with no name.
 *
 * This fork renders the same component, but uses the ARIA 1.2 pattern:
 * role="combobox" sits on the <input> itself, aria-expanded / aria-controls
 * ride with it, aria-owns is dropped (deprecated in 1.2 — aria-controls is
 * the canonical relationship).
 *
 * We call MUI's own `useAutocomplete` hook unchanged — keyboard, filtering,
 * multi-select, listbox logic all flow through it. Only the JSX that wires
 * the hook's output onto the DOM is forked. `styles` is borrowed from the
 * stock module so tenant CSS keeps targeting `MuiAutocomplete-*` classes.
 *
 * Delete this file and revert DployAutocomplete's import to
 * "@material-ui/lab/Autocomplete" when upgrading to MUI v5, which emits
 * ARIA 1.2 natively.
 */
const defaultCloseIcon = _jsx(CloseIcon, { fontSize: "small" });
const defaultPopupIcon = _jsx(ArrowDropDownIcon, {});
function DisablePortal(props) {
    const { anchorEl, open, ...other } = props;
    return _jsx("div", { ...other });
}
function Aria12Autocomplete(props, ref) {
    const { autoComplete = false, autoHighlight = false, autoSelect = false, blurOnSelect = false, ChipProps, classes, className, clearOnBlur = !props.freeSolo, clearOnEscape = false, clearText = "Clear", closeIcon = defaultCloseIcon, closeText = "Close", debug = false, defaultValue = props.multiple ? [] : null, disableClearable = false, disableCloseOnSelect = false, disabled = false, disabledItemsFocusable = false, disableListWrap = false, disablePortal = false, filterOptions, filterSelectedOptions = false, forcePopupIcon = "auto", freeSolo = false, fullWidth = false, getLimitTagsText = (more) => `+${more}`, getOptionDisabled, getOptionLabel = (x) => x, getOptionSelected, groupBy, handleHomeEndKeys = !props.freeSolo, id: idProp, includeInputInList = false, inputValue: inputValueProp, limitTags = -1, ListboxComponent = "ul", ListboxProps, loading = false, loadingText = "Loading…", multiple = false, noOptionsText = "No options", onChange, onClose, onHighlightChange, onInputChange, onOpen, open, openOnFocus = false, openText = "Open", options, PaperComponent = Paper, PopperComponent: PopperComponentProp = Popper, popupIcon = defaultPopupIcon, renderGroup: renderGroupProp, renderInput, renderOption: renderOptionProp, renderTags, selectOnFocus = !props.freeSolo, size = "medium", value: valueProp, ...other } = props;
    const PopperComponent = disablePortal ? DisablePortal : PopperComponentProp;
    const { getRootProps, getInputProps, getInputLabelProps, getPopupIndicatorProps, getClearProps, getTagProps, getListboxProps, getOptionProps, value, dirty, id, popupOpen, focused, focusedTag, anchorEl, setAnchorEl, inputValue, groupedOptions } = useAutocomplete({ ...props, componentName: "Autocomplete" });
    let startAdornment;
    if (multiple && value.length > 0) {
        const getCustomizedTagProps = (params) => ({
            className: clsx(classes.tag, size === "small" && classes.tagSizeSmall),
            disabled,
            ...getTagProps(params)
        });
        if (renderTags) {
            startAdornment = renderTags(value, getCustomizedTagProps);
        }
        else {
            startAdornment = value.map((option, index) => (_jsx(Chip, { label: getOptionLabel(option), size: size, ...getCustomizedTagProps({ index }), ...ChipProps })));
        }
    }
    if (limitTags > -1 && Array.isArray(startAdornment)) {
        const startArr = startAdornment;
        const more = startArr.length - limitTags;
        if (!focused && more > 0) {
            const truncated = startArr.slice(0, limitTags);
            truncated.push(_jsx("span", { className: classes.tag, children: getLimitTagsText(more) }, truncated.length));
            startAdornment = truncated;
        }
    }
    const defaultRenderGroup = (params) => (_jsxs("li", { children: [_jsx(ListSubheader, { className: classes.groupLabel, component: "div", children: params.group }), _jsx("ul", { className: classes.groupUl, children: params.children })] }, params.key));
    const renderGroup = renderGroupProp || defaultRenderGroup;
    const renderOption = renderOptionProp || getOptionLabel;
    const renderListOption = (option, index) => {
        const optionProps = getOptionProps({ option, index });
        return (_jsx("li", { ...optionProps, className: classes.option, children: renderOption(option, {
                selected: optionProps["aria-selected"],
                inputValue
            }) }));
    };
    const hasClearIcon = !disableClearable && !disabled;
    const hasPopupIcon = (!freeSolo || forcePopupIcon === true) && forcePopupIcon !== false;
    // ARIA 1.2: strip combobox-only attrs from the root. They move onto the
    // native <input> below via inputProps.
    const rootProps = getRootProps(other);
    const { role: _rootRole, "aria-owns": _rootAriaOwns, "aria-expanded": _rootAriaExpanded, "aria-haspopup": _rootAriaHasPopup, ...rootPropsWithoutCombobox } = rootProps;
    // Input-level combobox attrs. aria-controls / aria-activedescendant /
    // aria-autocomplete are already emitted by useAutocomplete's
    // getInputProps() — we only add role and aria-expanded here.
    // aria-expanded must always be present on a role=combobox element per
    // ARIA 1.2, so default to false when popup is closed.
    const comboboxInputProps = {
        role: "combobox",
        "aria-expanded": popupOpen ? "true" : "false"
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: ref, className: clsx(classes.root, className, focused && classes.focused, fullWidth && classes.fullWidth, hasClearIcon && classes.hasClearIcon, hasPopupIcon && classes.hasPopupIcon), ...rootPropsWithoutCombobox, children: renderInput({
                    id,
                    disabled,
                    fullWidth: true,
                    size: size === "small" ? "small" : undefined,
                    InputLabelProps: getInputLabelProps(),
                    InputProps: {
                        ref: setAnchorEl,
                        className: classes.inputRoot,
                        startAdornment,
                        endAdornment: (_jsxs("div", { className: classes.endAdornment, children: [hasClearIcon ? (_jsx(IconButton, { ...getClearProps(), "aria-label": clearText, title: clearText, className: clsx(classes.clearIndicator, dirty && classes.clearIndicatorDirty), children: closeIcon })) : null, hasPopupIcon ? (_jsx(IconButton, { ...getPopupIndicatorProps(), disabled: disabled, "aria-label": popupOpen ? closeText : openText, title: popupOpen ? closeText : openText, className: clsx(classes.popupIndicator, popupOpen && classes.popupIndicatorOpen), children: popupIcon })) : null] }))
                    },
                    inputProps: {
                        className: clsx(classes.input, focusedTag === -1 && classes.inputFocused),
                        disabled,
                        ...getInputProps(),
                        ...comboboxInputProps
                    }
                }) }), popupOpen && anchorEl ? (_jsx(PopperComponent, { className: clsx(classes.popper, disablePortal && classes.popperDisablePortal), style: { width: anchorEl ? anchorEl.clientWidth : undefined }, role: "presentation", anchorEl: anchorEl, open: true, children: _jsxs(PaperComponent, { className: classes.paper, children: [loading && groupedOptions.length === 0 ? (_jsx("div", { className: classes.loading, children: loadingText })) : null, groupedOptions.length === 0 && !freeSolo && !loading ? (_jsx("div", { className: classes.noOptions, children: noOptionsText })) : null, groupedOptions.length > 0 ? (_jsx(ListboxComponent, { className: classes.listbox, ...getListboxProps(), ...ListboxProps, children: groupedOptions.map((option, index) => {
                                if (groupBy) {
                                    return renderGroup({
                                        key: option.key,
                                        group: option.group,
                                        children: option.options.map((option2, index2) => renderListOption(option2, option.index + index2))
                                    });
                                }
                                return renderListOption(option, index);
                            }) })) : null] }) })) : null] }));
}
const Aria12AutocompleteForwarded = React.forwardRef(Aria12Autocomplete);
// Apply MUI's stock styles with the MuiAutocomplete name so tenant CSS
// overrides keep working unchanged.
const StyledAria12Autocomplete = withStyles(muiAutocompleteStyles, { name: "MuiAutocomplete" })(Aria12AutocompleteForwarded);
export default StyledAria12Autocomplete;
export { createFilterOptions } from "@material-ui/lab/Autocomplete";
//# sourceMappingURL=DployAutocompleteMui.js.map